import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Folder, Home, Maximize2, X } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, mediaUrl } from '../services/api';
import useBodyScrollLock from '../hooks/useBodyScrollLock';

function FolderCard({ folder, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(folder)}
      className="group flex min-h-36 flex-col items-center justify-center rounded-3xl border border-slate-200 bg-white p-5 text-center shadow-sm transition hover:-translate-y-1 hover:border-blue-300 hover:shadow-lg"
    >
      <span className="grid h-20 w-20 place-items-center overflow-hidden rounded-3xl bg-blue-50 text-[#005bbb] transition group-hover:bg-blue-100">
        {folder.icon_url ? (
          <img src={mediaUrl(folder.icon_url)} alt="" className="h-full w-full object-contain p-3" />
        ) : (
          <Folder className="h-11 w-11" strokeWidth={1.6} />
        )}
      </span>
      <span className="mt-4 line-clamp-2 text-sm font-extrabold leading-6 text-slate-800 sm:text-base">{folder.title}</span>
    </button>
  );
}

function ImageCarousel({ images }) {
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  useBodyScrollLock(lightbox);

  useEffect(() => { setIndex(0); setLightbox(false); }, [images]);

  useEffect(() => {
    if (!lightbox) return undefined;
    function onKeyDown(event) {
      if (event.key === 'Escape') setLightbox(false);
      if (event.key === 'ArrowLeft') setIndex((value) => (value + 1) % images.length);
      if (event.key === 'ArrowRight') setIndex((value) => (value - 1 + images.length) % images.length);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightbox, images.length]);

  const current = images[index];
  if (!current) return null;
  const previous = () => setIndex((value) => (value - 1 + images.length) % images.length);
  const next = () => setIndex((value) => (value + 1) % images.length);

  return (
    <>
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
        <div className="relative flex min-h-[350px] items-center justify-center bg-slate-50 p-4 sm:min-h-[520px] sm:p-8">
          <button type="button" onClick={() => setLightbox(true)} className="group relative flex h-full w-full items-center justify-center" aria-label="نمایش بزرگ تصویر">
            <img src={mediaUrl(current.image_url)} alt={current.title} className="max-h-[470px] max-w-full object-contain" />
            <span className="absolute left-2 top-2 grid h-10 w-10 place-items-center rounded-full bg-slate-900/70 text-white opacity-0 transition group-hover:opacity-100"><Maximize2 className="h-5 w-5" /></span>
          </button>
          {images.length > 1 && (
            <>
              <button type="button" onClick={previous} className="absolute right-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white text-slate-700 shadow-lg hover:text-[#005bbb]" aria-label="تصویر قبل"><ChevronRight className="h-6 w-6" /></button>
              <button type="button" onClick={next} className="absolute left-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white text-slate-700 shadow-lg hover:text-[#005bbb]" aria-label="تصویر بعد"><ChevronLeft className="h-6 w-6" /></button>
            </>
          )}
        </div>
        <div className="border-t border-slate-100 p-4 sm:p-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-extrabold text-slate-900">{current.size_label || current.title}</h2>
              <p className="mt-1 text-xs text-slate-500">{(index + 1).toLocaleString('fa-IR')} از {images.length.toLocaleString('fa-IR')}</p>
            </div>
            <button type="button" onClick={() => setLightbox(true)} className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-2 text-xs font-extrabold text-[#005bbb]">نمایش بزرگ</button>
          </div>
          {images.length > 1 && (
            <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
              {images.map((image, imageIndex) => (
                <button key={image.id} type="button" onClick={() => setIndex(imageIndex)} className={`h-20 w-24 shrink-0 overflow-hidden rounded-2xl border-2 bg-slate-50 p-1 transition ${imageIndex === index ? 'border-[#005bbb]' : 'border-transparent hover:border-blue-200'}`}>
                  <img src={mediaUrl(image.image_url)} alt={image.title} loading="lazy" className="h-full w-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/95 p-3 sm:p-8" onMouseDown={() => setLightbox(false)}>
          <button type="button" onClick={() => setLightbox(false)} className="absolute left-4 top-4 z-10 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20" aria-label="بستن"><X className="h-6 w-6" /></button>
          <div className="relative flex h-full w-full items-center justify-center" onMouseDown={(event) => event.stopPropagation()}>
            <img src={mediaUrl(current.image_url)} alt={current.title} className="max-h-full max-w-full object-contain" />
            {images.length > 1 && (
              <>
                <button type="button" onClick={previous} className="absolute right-2 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"><ChevronRight className="h-7 w-7" /></button>
                <button type="button" onClick={next} className="absolute left-2 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"><ChevronLeft className="h-7 w-7" /></button>
              </>
            )}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-4 py-2 text-sm font-bold text-white">{current.size_label || current.title}</div>
          </div>
        </div>
      )}
    </>
  );
}

export default function EngineeringLibraryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ node: null, breadcrumbs: [], children: [], images: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    const request = id ? api.getEngineeringLibraryNode(id) : api.getEngineeringLibraryRoot();
    request
      .then((result) => { if (mounted) setData(result?.data || { node: null, breadcrumbs: [], children: [], images: [] }); })
      .catch((err) => { if (mounted) setError(err?.status === 404 ? 'مسیر موردنظر پیدا نشد.' : 'دریافت اطلاعات راهنمای فنی ناموفق بود.'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id]);

  const pageTitle = useMemo(() => data.node?.title || 'دانشنامه فنی پایپینگ', [data.node]);

  return (
    <main className="min-h-[70vh] bg-gradient-to-b from-slate-50 to-white pb-16 pt-8" dir="rtl">
      <div className="pp-container">
        <div className="rounded-[2rem] border border-blue-100 bg-white p-6 shadow-sm sm:p-8">
          <div className="text-xs font-extrabold tracking-wide text-[#005bbb]">مرجع اطلاعات مهندسی پارس پایپینگ</div>
          <h1 className="mt-3 text-2xl font-black text-slate-900 sm:text-4xl">{pageTitle}</h1>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600 sm:text-base">برای مشاهده اطلاعات فنی و تصاویر سایزبندی، مسیر موردنظر را مرحله‌به‌مرحله انتخاب کنید.</p>
        </div>

        <nav className="my-5 flex flex-wrap items-center gap-2 text-sm" aria-label="مسیر راهنمای فنی">
          <Link to="/engineering-library" className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 font-bold text-slate-600 shadow-sm hover:text-[#005bbb]"><Home className="h-4 w-4" /> دانشنامه فنی پایپینگ</Link>
          {(data.breadcrumbs || []).map((crumb) => (
            <React.Fragment key={crumb.id}>
              <ChevronLeft className="h-4 w-4 text-slate-300" />
              <Link to={`/engineering-library/${crumb.id}`} className="rounded-xl px-2 py-2 font-bold text-slate-600 hover:bg-white hover:text-[#005bbb]">{crumb.title}</Link>
            </React.Fragment>
          ))}
        </nav>

        {loading ? (
          <div className="rounded-[2rem] border border-slate-200 bg-white p-12 text-center text-sm text-slate-500">در حال دریافت اطلاعات...</div>
        ) : error ? (
          <div className="rounded-[2rem] border border-red-100 bg-red-50 p-8 text-center text-sm font-bold text-red-700">{error}</div>
        ) : (
          <div className="space-y-7">
            {data.children?.length > 0 && (
              <section>
                <div className="mb-4 flex items-end justify-between gap-4">
                  <div><h2 className="text-xl font-black text-slate-900">انتخاب دسته</h2><p className="mt-1 text-sm text-slate-500">برای ادامه روی پوشه موردنظر کلیک کنید.</p></div>
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-[#005bbb]">{data.children.length.toLocaleString('fa-IR')} پوشه</span>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {data.children.map((folder) => <FolderCard key={folder.id} folder={folder} onOpen={(item) => navigate(`/engineering-library/${item.id}`)} />)}
                </div>
              </section>
            )}

            {data.images?.length > 0 && (
              <section>
                <div className="mb-4"><h2 className="text-xl font-black text-slate-900">اطلاعات سایزبندی</h2><p className="mt-1 text-sm text-slate-500">تصاویر فنی مربوط به این دسته را ورق بزنید یا برای مشاهده کامل بزرگ کنید.</p></div>
                <ImageCarousel images={data.images} />
              </section>
            )}

            {!data.children?.length && !data.images?.length && (
              <div className="rounded-[2rem] border border-dashed border-blue-200 bg-blue-50/40 p-12 text-center">
                <Folder className="mx-auto h-12 w-12 text-blue-300" />
                <h2 className="mt-4 font-black text-slate-800">اطلاعات این بخش در حال تکمیل است</h2>
                <p className="mt-2 text-sm text-slate-500">تصاویر فنی این مسیر به‌زودی اضافه می‌شوند.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
